import type { Mint } from "@solana/spl-token0.3.8";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import {
  NATIVE_SOL,
  STEP_FEE_ADDRESS,
  STEP_MINT,
  toLamports,
  USDC_MINT,
  WRAPPED_SOL_MINT,
  XSTEP_MINT,
} from "@stepfinance/common";
import { deformatNumber } from "@stepfinance/react";
import { CurveType } from "@stepfinance/step-swap";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import BigNumber from "bignumber.js";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAccountByMint, useSwappableTokens } from "../hooks";
import { calculateUnstakeFee } from "../models/marinade/utils";
import type { PoolConfig } from "../models/swap";
import { DEFAULT_DENOMINATOR } from "../models/swap";
import { cache } from "../utils/cache";
import type { AMMSwapRoute } from "../utils/pools";
import { calculateDependentAmount, PoolOperation } from "../utils/pools";
import { getTokenIcon, getTokenName } from "../utils/tokenInfo";
import { useLocalStorageState } from "../utils/utils";
import { useNetworkConfig, useSlippageConfig } from "./app/config";
import { useLiquidity, usePoolsForBasket } from "./liquidity";
import { useXStepContext } from "./xStep";

export interface CurrencyContextState {
  mintAddress: PublicKey;
  account?: NormalizedTokenAccount;
  mint?: Mint;
  amount: string;
  name: string;
  icon?: string;
  setAmount: (val: string) => void;
  setMint: (mintAddress: PublicKey) => void;
  convertAmount: () => number;
  sufficientBalance: () => boolean;
}

export enum SwapRoute {
  NONE,
  AMM,
  DEX,
  WRAP, // Special case for wrapping/unwrapping SOL
  STAKE, // Step Staking
}

export type SwapRouteContext = {
  amountOut: number;
  type: SwapRoute;
};

export interface SwapContextState {
  // Mint being traded from. The user must own these tokens.
  A: CurrencyContextState;
  // Mint being traded to. The user will receive these tokens after the swap.
  B: CurrencyContextState;
  // the route taken by the AMM for a given swap
  route?: AMMSwapRoute[];

  // Function to flip what we consider to be the "to" and "from" mints.
  swapToFromMints: () => void;

  // If swap can be routed through DEX
  canSwapWithDEX: boolean;

  // The amount (in units of percent) a swap can be off from the estimate
  // shown to the user.
  slippage: number;

  // The referral *owner* address. Associated token accounts must be created,
  // first, for this to be used.
  referral?: PublicKey;

  // True if all newly created market accounts should be closed in the
  // same user flow (ideally in the same transaction).
  isClosingNewAccounts: boolean;
  setIsClosingNewAccounts: (b: boolean) => void;

  // True if the swap exchange rate should be a function of nothing but the
  // from and to tokens, ignoring any quote tokens that may have been
  // accumulated by performing the swap.
  //
  // Always false (for now).
  isStrict: boolean;
  setIsStrict: (isStrict: boolean) => void;

  swapRouteContext: SwapRouteContext;

  lastTypedAccount: PublicKey;
  setLastTypedAccount: (mintAddress: PublicKey) => void;
  recalculateDependent: () => void;
  setPoolOperation: (swapDirection: PoolOperation) => void;
  options: PoolConfig;
  setOptions: (config: PoolConfig) => void;
  marinadeUnstakeFee: number;
}

const SwapContext = React.createContext<SwapContextState | null>(null);

export const useCurrencyLeg = (config: PoolConfig, defaultMint: PublicKey, cacheKey: string) => {
  const [amount, setAmount] = useState("");
  const [mintAddress, setMintAddress] = useState(defaultMint);
  const account = useAccountByMint(mintAddress.toBase58());
  const mint = cache.getMint(mintAddress);
  const [_, setCachedMint] = useLocalStorageState(cacheKey, defaultMint.toBase58());

  return useMemo(
    () => ({
      mintAddress: mintAddress,
      account: account,
      mint: mint,
      amount: amount,
      name: getTokenName(mintAddress),
      icon: getTokenIcon(mintAddress),
      setAmount: setAmount,
      setMint: (mint: PublicKey) => {
        setMintAddress(mint);
        setCachedMint(mint.toBase58());
      },
      convertAmount: () => toLamports(deformatNumber(amount), mint),
      sufficientBalance: () =>
        account !== undefined &&
        (account.tokenAmount.uiAmount >= deformatNumber(amount) ||
          config.curveType === CurveType.ConstantProductWithOffset),
    }),
    [mintAddress, account, mint, amount, setAmount, setMintAddress, setCachedMint, config],
  );
};

const useQuery = () => new URLSearchParams(useLocation().search);

export const SwapContextProvider = ({ children }) => {
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const { slippage } = useSlippageConfig();

  const DEFAULT_QUOTE_MINT = network === "devnet" ? NATIVE_SOL : USDC_MINT;

  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const { swappableTokensMap } = useSwappableTokens();
  const [lastTypedAccount, setLastTypedAccount] = useState(DEFAULT_QUOTE_MINT);
  const [poolOperation, setPoolOperation] = useState<PoolOperation>(PoolOperation.Add);
  const xStepData = useXStepContext();
  const { marinadeState } = useLiquidity();
  const mSOLPricePerSOL = marinadeState?.mSOLPricePerSOL ?? 1;
  const [marinadeUnstakingFee, setMarinadeUnstakingFee] = useState(0.003);

  const [isClosingNewAccounts, setIsClosingNewAccounts] = useState(false);
  const [isStrict, setIsStrict] = useState(false);

  const [defaultQuoteMint, _setDefaultQuoteMint] = useLocalStorageState(
    "defaultQuoteMint",
    DEFAULT_QUOTE_MINT.toBase58(),
  );
  const [defaultBaseMint, _setDefaultBaseMint] = useLocalStorageState("defaultBaseMint", STEP_MINT.toBase58());

  const [swapRouteContext, setSwapRouteContext] = useState<SwapRouteContext>({
    amountOut: 0,
    type: SwapRoute.NONE,
  });

  const [recalculateDependent, setRecalculateDependent] = useState(false);

  const [options, setOptions] = useState<PoolConfig>({
    curveType: CurveType.ConstantProduct,
    fees: {
      tradeFeeNumerator: 20,
      tradeFeeDenominator: DEFAULT_DENOMINATOR,
      ownerTradeFeeNumerator: 10,
      ownerTradeFeeDenominator: DEFAULT_DENOMINATOR,
      ownerWithdrawFeeNumerator: 0,
      ownerWithdrawFeeDenominator: 0,
      hostFeeNumerator: 20,
      hostFeeDenominator: 100,
    },
  });

  const base = useCurrencyLeg(options, new PublicKey(defaultQuoteMint), "defaultQuoteMint");
  const mintAddressA = base.mintAddress;
  const setMintAddressA = base.setMint;
  const amountA = base.amount;
  const setAmountA = base.setAmount;

  const quote = useCurrencyLeg(options, new PublicKey(defaultBaseMint), "defaultBaseMint");
  const mintAddressB = quote.mintAddress;
  const setMintAddressB = quote.setMint;
  const amountB = quote.amount;
  const setAmountB = quote.setAmount;

  const pools = usePoolsForBasket(
    lastTypedAccount.equals(base.mintAddress)
      ? [base.mintAddress, quote.mintAddress]
      : [quote.mintAddress, base.mintAddress],
  );

  useEffect(() => {
    if ((pools?.length ?? 0) > 0) {
      setRecalculateDependent((recalculateDependent) => !recalculateDependent);
    }
  }, [pools]);

  useEffect(() => {
    const isValidPath = location.pathname.includes("/swap") || location.pathname.includes("/liquidity");

    if (isValidPath && swappableTokensMap.size > 0) {
      let hasValidQueryParams = false;
      const mintA = query.get("a");
      const mintB = query.get("b");
      if (mintA && swappableTokensMap.has(mintA)) {
        setMintAddressA(new PublicKey(mintA));
        hasValidQueryParams = true;
      }
      if (mintB && swappableTokensMap.has(mintB)) {
        setMintAddressB(new PublicKey(mintB));
        hasValidQueryParams = true;
      }

      if (hasValidQueryParams) {
        navigate({ search: "" }, { replace: true });
      }
    }
  }, [navigate, location.pathname, swappableTokensMap, query, setMintAddressA, setMintAddressB]);

  const swapToFromMints = () => {
    const tempMint = base.mintAddress;
    const tempAmount = base.amount;
    setLastTypedAccount(quote.mintAddress);
    base.setMint(quote.mintAddress);
    base.setAmount(quote.amount);
    quote.setMint(tempMint);
    quote.setAmount(tempAmount);
    setPoolOperation((op: PoolOperation) => {
      switch (op) {
        case PoolOperation.SwapGivenInput:
          return PoolOperation.SwapGivenProceeds;
        case PoolOperation.SwapGivenProceeds:
          return PoolOperation.SwapGivenInput;
        case PoolOperation.Add:
          return PoolOperation.SwapGivenInput;
        case PoolOperation.StepStaking:
          return PoolOperation.StepStaking;
        case PoolOperation.MarinadeStake:
          return PoolOperation.MarinadeStake;
        case PoolOperation.MarinadeUnstake:
          return PoolOperation.MarinadeUnstake;
      }
    });
  };

  const calculateDependent = useCallback(async () => {
    let ammAmountOut = 0;
    let setDependent;
    let amount: number;
    let independent: PublicKey;
    if (lastTypedAccount.equals(mintAddressA)) {
      independent = mintAddressA;
      amount = parseFloat(amountA);
      setDependent = setAmountB;
    } else {
      independent = mintAddressB;
      amount = parseFloat(amountB);
      setDependent = setAmountA;
    }

    let swapRoute: SwapRoute = SwapRoute.NONE;
    let amountOut = 0;

    if (
      (mintAddressA.equals(NATIVE_SOL) || mintAddressA.equals(WRAPPED_SOL_MINT)) &&
      (mintAddressB.equals(NATIVE_SOL) || mintAddressB.equals(WRAPPED_SOL_MINT))
    ) {
      swapRoute = SwapRoute.WRAP;
      amountOut = amount;
      if (Number.isNaN(amountOut)) {
        amountOut = 0;
      }
      setDependent(amountOut === 0 ? "" : amountOut.toFixed(9));
    } else if (poolOperation === PoolOperation.MarinadeStake || poolOperation === PoolOperation.MarinadeUnstake) {
      let amount: number;
      let independent: PublicKey;
      if (lastTypedAccount.equals(mintAddressA)) {
        independent = mintAddressA;
        amount = parseFloat(amountA);
      } else {
        independent = mintAddressB;
        amount = parseFloat(amountB);
      }
      swapRoute = SwapRoute.STAKE;

      amountOut = independent.equals(NATIVE_SOL) ? amount / mSOLPricePerSOL : amount * mSOLPricePerSOL;
      let unstakingFee = 0.003;
      if (poolOperation === PoolOperation.MarinadeUnstake) {
        unstakingFee = await calculateUnstakeFee(connection, new BigNumber(toLamports(amountOut, 9)));
        amountOut = independent.equals(NATIVE_SOL)
          ? amountOut * (1 + unstakingFee / 10000)
          : amountOut * (1 - unstakingFee / 10000);
      }
      setMarinadeUnstakingFee(unstakingFee / 10000);
      setDependent(Number.isNaN(amountOut) || amountOut === 0 ? "" : amountOut.toFixed(9));
    } else if (poolOperation === PoolOperation.StepStaking) {
      let amount: number;
      let independent: PublicKey;
      if (lastTypedAccount.equals(mintAddressA)) {
        independent = mintAddressA;
        amount = parseFloat(amountA);
      } else {
        independent = mintAddressB;
        amount = parseFloat(amountB);
      }
      swapRoute = SwapRoute.STAKE;

      amountOut = independent.equals(XSTEP_MINT) ? amount * xStepData.stepPerXStep : amount / xStepData.stepPerXStep;

      setDependent(Number.isNaN(amountOut) || amountOut === 0 ? "" : amountOut.toFixed(9));
    } else if (poolOperation !== PoolOperation.SwapGivenInput && poolOperation !== PoolOperation.SwapGivenProceeds) {
      if (pools?.length) {
        const { amountOut, error } = await calculateDependentAmount(
          connection,
          independent,
          amount,
          pools,
          poolOperation,
        );

        if (error) {
          console.error("SwapContextProvider Error:", error);
          if (poolOperation === PoolOperation.Add) {
            return;
          }
        }

        ammAmountOut = amountOut;
      } else {
        if (poolOperation === PoolOperation.Add) {
          return;
        }
      }

      swapRoute = SwapRoute.AMM;
      amountOut = ammAmountOut;
      if (amountOut === 0) {
        setDependent("");
      } else {
        setDependent(ammAmountOut.toFixed(6));
      }
    }

    setSwapRouteContext({
      amountOut: Number.isNaN(amountOut) ? 0 : amount,
      type: swapRoute,
    });
  }, [
    lastTypedAccount,
    mintAddressA,
    mintAddressB,
    poolOperation,
    amountA,
    setAmountB,
    amountB,
    setAmountA,
    mSOLPricePerSOL,
    xStepData.stepPerXStep,
    pools,
    connection,
  ]);

  useEffect(() => {
    void calculateDependent();
  }, [amountB, amountA, lastTypedAccount, recalculateDependent, poolOperation, calculateDependent]);

  return (
    <SwapContext.Provider
      value={{
        A: base,
        B: quote,
        swapToFromMints,
        canSwapWithDEX: false,
        referral: STEP_FEE_ADDRESS,
        slippage,
        isClosingNewAccounts,
        setIsClosingNewAccounts,
        isStrict,
        setIsStrict,
        swapRouteContext,
        lastTypedAccount,
        setLastTypedAccount,
        recalculateDependent: () => setRecalculateDependent(!recalculateDependent),
        setPoolOperation,
        options,
        setOptions,
        marinadeUnstakeFee: marinadeUnstakingFee,
      }}
    >
      {children}
    </SwapContext.Provider>
  );
};

export const useSwapContextState = () => {
  const context = useContext(SwapContext);
  return context as SwapContextState;
};
